/**
 * SCAP (Signage Common Application Platform) wrapper for React.
 * Uses window.Storage, window.InputSource, window.Signage from SCAP_Library-official-1.8.1.
 * All SCAP methods are callback-based; this module provides Promise-based helpers.
 */

/**
 * Call a SCAP instance method (successCallback, failureCallback, options) as a Promise.
 * @param {object|null} instance - SCAP instance (e.g. new Storage(), new InputSource(), new Signage())
 * @param {string} methodName - Method name (e.g. 'listFiles', 'copyFile', 'changeInputSource')
 * @param {object} [options={}] - Options passed as third argument
 * @returns {Promise<any>}
 */
export function scapCall(instance, methodName, options = {}) {
  return new Promise((resolve, reject) => {
    if (!instance || typeof instance[methodName] !== 'function') {
      reject(new Error('SCAP not available'));
      return;
    }
    instance[methodName](resolve, reject, options);
  });
}

/**
 * @returns {boolean} True if SCAP (Cordova + Storage) is available (e.g. on webOS Signage).
 */
export function isScapAvailable() {
  if (typeof window === 'undefined') return false;
  try {
    return typeof window.Storage === 'function' && typeof window.cordova !== 'undefined';
  } catch {
    return false;
  }
}

/**
 * @returns {import('cordova/plugin/storage')|null} Storage instance or null if not available.
 */
export function getStorage() {
  if (typeof window === 'undefined' || typeof window.Storage !== 'function') return null;
  try {
    return new window.Storage();
  } catch {
    return null;
  }
}

/**
 * @returns {import('cordova/plugin/inputSource')|null} InputSource instance or null if not available.
 */
export function getInputSource() {
  if (typeof window === 'undefined' || typeof window.InputSource !== 'function') return null;
  try {
    return new window.InputSource();
  } catch {
    return null;
  }
}

/**
 * @returns {import('cordova/plugin/signage')|null} Signage instance or null if not available.
 */
export function getSignage() {
  if (typeof window === 'undefined' || typeof window.Signage !== 'function') return null;
  try {
    return new window.Signage();
  } catch {
    return null;
  }
}

/** Default storage path for signage content (local storage). */
export const DEFAULT_STORAGE_PATH = 'file://internal/';

/** Path often used for app content on webOS Signage. */
export const CONTENT_PATH = 'file://internal/content';

/**
 * List files in a directory via SCAP Storage.
 * @param {string} [pathURI='file://internal/'] - URI (e.g. file://internal/, file://internal/content)
 * @returns {Promise<{ files: Array<{ name: string, type: 'file'|'folder', size: number }>, totalCount: number }>}
 */
export function listStorageFiles(pathURI = DEFAULT_STORAGE_PATH) {
  const storage = getStorage();
  if (!storage) return Promise.reject(new Error('SCAP Storage not available'));
  return scapCall(storage, 'listFiles', { path: pathURI });
}
